import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import SectionTitle from '@/components/base/SectionTitle';
import NewsListItem from '@/components/feature/NewsListItem';
import VideoCard from '@/components/feature/VideoCard';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { getHotVideos, searchNews, searchVideos } from '@/utils/media';

const TABS = [
  { key: 'all', label: '全部' },
  { key: 'movie', label: '电影' },
  { key: 'tv', label: '电视剧' },
  { key: 'variety', label: '综艺' },
  { key: 'news', label: '资讯' },
];

const SORTS = [
  { key: 'relevance', label: '按相关度' },
  { key: 'rating', label: '按评分' },
  { key: 'new', label: '按更新时间' },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const [input, setInput] = useState(query);
  const [tab, setTab] = useState('all');
  const [sort, setSort] = useState('relevance');

  useDocumentMeta(
    query ? `【${query}】的搜索结果 - 黄牛视频` : '搜索影视 - 黄牛视频',
    query
      ? `黄牛视频关于“${query}”的搜索结果，包含相关电影、电视剧、综艺与影视资讯。`
      : '在黄牛视频搜索电影、电视剧、综艺和影视资讯，快速找到你想看的内容。',
    '影视搜索,黄牛视频搜索,电影搜索,电视剧搜索',
  );

  useEffect(() => {
    setInput(query);
    setTab('all');
  }, [query]);

  const videoResults = useMemo(() => {
    if (!query) return [];
    const type = tab === 'news' ? 'all' : (tab as 'all' | 'movie' | 'tv' | 'variety');
    const list = searchVideos(query, type);
    return [...list].sort((a, b) => {
      if (sort === 'rating') return b.rating - a.rating;
      if (sort === 'new') return a.updatedAt < b.updatedAt ? 1 : -1;
      return 0;
    });
  }, [query, tab, sort]);

  const newsResults = useMemo(() => {
    if (!query) return [];
    if (tab !== 'all' && tab !== 'news') return [];
    return searchNews(query);
  }, [query, tab]);

  const total = videoResults.length + newsResults.length;
  const hot = getHotVideos(6);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (q) setSearchParams({ q });
    else setSearchParams({});
  };

  const chipBase = 'rounded-full px-4 py-1.5 text-sm transition-colors whitespace-nowrap';

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索' }]} />

      <SectionTitle
        title="搜索影视"
        subtitle="输入关键词，搜索电影、电视剧、综艺与影视资讯。"
        asHeading="h1"
      />

      <form onSubmit={submit} className="mb-6">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 items-center gap-3 rounded-md border border-background-200 bg-background-100 px-4 py-3 transition-colors focus-within:border-primary-400">
            <i className="ri-search-line text-lg text-foreground-400" />
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="搜索作品名称、导演、演员或类型"
              className="w-full bg-transparent text-sm text-foreground-100 placeholder:text-foreground-500 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            className="flex items-center justify-center gap-2 rounded-md bg-primary-500 px-8 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            <i className="ri-search-line" />
            搜索
          </button>
        </div>
      </form>

      <div className="mb-6 flex flex-wrap items-center gap-2 rounded-lg border border-background-200 bg-background-100 p-3">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`${chipBase} ${
              tab === t.key
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-200 text-foreground-300 hover:text-foreground-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!query ? (
        <EmptyState
          icon="ri-search-2-line"
          title="输入关键词开始搜索"
          description="例如：星河长歌、悬疑、秦朗，都可以试试。"
        />
      ) : total === 0 ? (
        <>
          <EmptyState
            icon="ri-emotion-sad-line"
            title={`没有找到与“${query}”相关的内容`}
            description="换个关键词试试，或者看看下面这些热门推荐。"
          />
          <section className="mt-12">
            <SectionTitle title="为你推荐" icon="ri-fire-line" />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {hot.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </section>
        </>
      ) : (
        <div className="space-y-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-500">
              找到 <span className="font-semibold text-primary-500">{total}</span> 条与“
              {query}”相关的结果
            </p>
            {tab !== 'news' && (
              <div className="flex flex-wrap items-center gap-2">
                {SORTS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setSort(s.key)}
                    className={`${chipBase} ${
                      sort === s.key
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-background-200 text-foreground-300 hover:text-foreground-50'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {videoResults.length > 0 && (
            <div>
              <h2 className="mb-4 text-lg font-semibold text-foreground-50">
                影视作品（{videoResults.length}）
              </h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                {videoResults.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </div>
          )}

          {newsResults.length > 0 && (
            <div>
              <h2 className="mb-4 text-lg font-semibold text-foreground-50">
                影视资讯（{newsResults.length}）
              </h2>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {newsResults.map((item) => (
                  <NewsListItem key={item.id} item={item} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}